import React, { useEffect, useState } from "react";
import styles from "../../css/AddNewAdjustment.module.css";
import { ReactComponent as DeleteIcon } from "../../assets/icons/delete_icon_primary.svg";
import { ReactComponent as PlusIcon } from "../../assets/icons/plusBlack.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import buttoncss from "../../../myfin_v2/css/button.module.css";
import { Button } from "../../../myfin_v2/elements/Button";
import Dropdown from "../../../myfin_v2/elements/Dropdown";
import {
  getCashflowDropdowns,
  createAddAjustment,
  updateAddAjustment,
  deleteCashflowAdjustment,
  getPopulateFSDetailsApiHandler
} from "../../../WorkSpace/Components/ApiServices/apiHandler";
import swal from "sweetalert";

const defaultEntry = () => ({
  section: "",
  cashflowRowID: "",
  amount: "",
  previousYear: ""
});

const formatIndianNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(value);
  if (isNaN(number)) return value;
  const formatted = Math.abs(number).toLocaleString("en-IN");
  return number < 0 ? `(${formatted})` : formatted;
};

const parseNumber = (val) => {
  if (!val) return "";
  let clean = val.replace(/,/g, "");
  if (clean.includes("(") && clean.includes(")")) {
    clean = "-" + clean.replace(/[()]/g, "");
  }
  return clean;
};

const defaultRow = () => ({
  description: "",
  adjustmentID: null,
  entries: [defaultEntry(), defaultEntry()]
});

const AddNewAdjustment = ({ projectID }) => {
  const [items, setItems] = useState([defaultRow()]);
  const [dropdownData, setDropdownData] = useState([]);

  const fetchData = async () => {
    try {
      // 1. Fetch Dropdowns
      const dropdownRes = await getCashflowDropdowns(projectID);
      const options = dropdownRes?.data?.options || [];
      setDropdownData(options);

      // 2. Fetch Adjustments
      const adjRes = await getPopulateFSDetailsApiHandler({
        projectID,
        category: "cashflowAdjustments"
      });
      const adjustments = adjRes?.data?.data || adjRes?.data?.cashflowAdjustments || [];

      if (adjustments && adjustments.length > 0) {
        const mapped = adjustments.map((adj) => {
          const entries = adj.entries.map((ent) => {
            const sectionObj = options.find((sec) =>
              sec.rows.some((r) => r.rowID === ent.cashflowRowID)
            );
            return {
              section: sectionObj ? (sectionObj._id || sectionObj.section) : "",
              cashflowRowID: ent.cashflowRowID,
              amount: ent.amount ?? "",
              previousYear: ent.previousYear ?? ""
            };
          });

          // Ensure at least 2 entries are shown
          while (entries.length < 2) {
            entries.push(defaultEntry());
          }

          return {
            description: adj.description,
            adjustmentID: adj._id,
            entries
          };
        });

        // Show empty row at top, followed by existing adjustments
        setItems([defaultRow(), ...mapped]);
      } else {
        setItems([defaultRow()]);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectID]);

  const handleAdd = () => {
    setItems((prev) => [...prev, defaultRow()]);
  };

  const handleAddEntry = (index) => {
    const updated = [...items];
    updated[index].entries.push(defaultEntry());
    setItems(updated);
  };

  const handleRemoveEntry = (index, entryIndex) => {
    const updated = [...items];
    updated[index].entries = updated[index].entries.filter((_, i) => i !== entryIndex);
    setItems(updated);
  };

  const handleChange = (index, field, value) => {
    const updated = [...items];
    updated[index][field] = value;
    setItems(updated);
  };

  const handleEntryChange = (index, entryIndex, field, value) => {
    const updated = [...items];
    const entry = updated[index].entries[entryIndex];

    if (field === "section") {
      entry.section = value;
      entry.cashflowRowID = "";
    } else {
      entry[field] = value;
    }

    setItems(updated);
  };

  const handleSave = async (index) => {
    const item = items[index];

    const sum = item.entries.reduce((acc, entry) => acc + (Number(entry.amount) || 0), 0);
    if (Math.abs(sum) > 0.01) {
      swal("Unbalanced ⚠️", "Current-year amounts must sum to zero. Current total: " + formatIndianNumber(sum), "warning");
      return;
    }

    // Filter out rows that are effectively empty (no selection and zero/empty amounts)
    const hasValue = (v) => v && v !== "" && Number(v) !== 0;
    const entriesWithData = item.entries.filter(e => e.cashflowRowID || hasValue(e.amount) || hasValue(e.previousYear));
    
    if (entriesWithData.length < 2) {
      swal("Incomplete Adjustment", "At least two balanced entries (one debit and one credit) are required.", "warning");
      return;
    }

    // Check for missing selections in rows that have data
    const missingRows = entriesWithData
      .map((e, i) => (!e.cashflowRowID ? i + 1 : null))
      .filter(n => n !== null);

    if (missingRows.length > 0) {
      swal({
        title: "Selection Required",
        text: `Row ${missingRows.join(", ")} is missing a 'Row' selection. Please ensure all entries have a selected destination.`,
        icon: "warning"
      });
      return;
    }

    const payload = {
      description: item.description,
      entries: entriesWithData.map((entry) => ({
        cashflowRowID: entry.cashflowRowID,
        amount: Number(entry.amount) || 0,
        previousYear: Number(entry.previousYear) || 0
      }))
    };

    try {
      let res;
      if (item.adjustmentID) {
        res = await updateAddAjustment(
          { projectID, adjustmentID: item.adjustmentID },
          payload
        );
        swal("Updated ✅", res?.data?.message || "Adjustment updated", "success");
        fetchData();
      } else {
        res = await createAddAjustment({ projectID }, payload);
        
        // Extract the new ID from the response robustly
        const responseData = res?.data?.data || res?.data;
        let newId = null;
        if (Array.isArray(responseData)) {
          newId = responseData[responseData.length - 1]?._id;
        } else if (responseData?.cashflowAdjustments && Array.isArray(responseData.cashflowAdjustments)) {
          newId = responseData.cashflowAdjustments[responseData.cashflowAdjustments.length - 1]?._id;
        } else {
          newId = responseData?._id || responseData?.id;
        }
        
        if (newId) {
          swal("Created ✅", res?.data?.message || "Adjustment created", "success");
          fetchData();
        } else {
          console.error("Could not find ID in response", res.data);
          getPopulateFSDetailsApiHandler(); // Fallback to full fetch
        }
      }
    } catch (err) {
      swal("Error ❌", err?.response?.data?.message || "Something went wrong", "error");
    }
  };

  const handleDelete = async (index) => {
    const item = items[index];
    const hasData = item.description || item.entries.some(e => e.cashflowRowID || e.amount || e.previousYear);

    // If card is empty and unsaved, just remove it immediately
    if (!item.adjustmentID && !hasData) {
      if (items.length > 1) {
        setItems((prev) => prev.filter((_, i) => i !== index));
      } else {
        setItems([defaultRow()]);
      }
      return;
    }

    // Otherwise, ask for confirmation
    const confirm = await swal({
      title: "Are you sure?",
      text: item.adjustmentID 
        ? "Once deleted, you will not be able to recover this adjustment from the database!"
        : "Are you sure you want to remove this adjustment card?",
      icon: "warning",
      buttons: ["Cancel", "Yes, Delete"],
      dangerMode: true,
    });

    if (confirm) {
      if (item.adjustmentID) {
        try {
          await deleteCashflowAdjustment({
            projectID,
            adjustmentID: String(item.adjustmentID)
          });
          setItems((prev) => prev.filter((_, i) => i !== index));
          swal("Deleted ✅", "The adjustment has been successfully removed.", "success");
        } catch (err) {
          swal("Error ❌", err?.response?.data?.message || "Failed to delete from database", "error");
        }
      } else {
        // Just remove from UI
        if (items.length > 1) {
          setItems((prev) => prev.filter((_, i) => i !== index));
        } else {
          setItems([defaultRow()]);
        }
        swal("Removed ✅", "The card has been removed.", "success");
      }
    }
  };

  return (
    <div className={styles.container} style={{ paddingLeft: "0px" }}>
      
      <div className={styles.header}>
        <Button
          btnColor={buttoncss.primary}
          label="+ Add New Item"
          onClick={handleAdd}
        />
      </div>

      {items.map((item, index) => (
        <div key={index} className={styles.card}>
          <div className={styles.formGrid}>

            {/* Description */}
            <div className={styles.label}>Adjustment Description</div>

            <input
              className={styles.input}
              placeholder="Enter description"
              value={item.description}
              onChange={(e) =>
                handleChange(index, "description", e.target.value)
              }
            />

            <div className={styles.actionGroup}>
              <DeleteIcon
                className={styles.deleteIcon}
                onClick={() => handleDelete(index)}
              />

              <Button
                className={styles.saveBtn}
                label="Save"
                onClick={() => handleSave(index)}
              />
            </div>

            {item.entries.map((entry, entryIndex) => (
              <React.Fragment key={entryIndex}>
                <div className={styles.label}>Adjustment to be shown under</div>

                <div className={`${styles.rowFields} ${styles.fullRow}`}>
                  <Dropdown
                    class_={styles.select}
                    class2_={styles.dropdownInner}
                    placeHolder="Select Section"
                    options={dropdownData.map((sec) => ({
                      label: sec.section,
                      value: sec._id || sec.section
                    }))}
                    selValue={entry.section}
                    searchable={true}
                    onSelect={(val) =>
                      handleEntryChange(index, entryIndex, "section", val?.value || "")
                    }
                  />

                  <Dropdown
                    class_={styles.select}
                    class2_={styles.dropdownInner}
                    placeHolder="Select Row"
                    options={
                      dropdownData
                        .find((s) => (s._id || s.section) === entry.section)
                        ?.rows.map((row) => ({
                          label: row.row,
                          value: row.rowID
                        })) || []
                    }
                    selValue={entry.cashflowRowID}
                    searchable={true}
                    onSelect={(val) =>
                      handleEntryChange(index, entryIndex, "cashflowRowID", val?.value || "")
                    }
                  />

                  <input
                    className={styles.amountInput}
                    placeholder="Amount"
                    value={formatIndianNumber(entry.amount)}
                    onChange={(e) =>
                      handleEntryChange(
                        index,
                        entryIndex,
                        "amount",
                        parseNumber(e.target.value)
                      )
                    }
                  />

                  <input
                    className={styles.amountInput}
                    placeholder="Previous Year"
                    value={formatIndianNumber(entry.previousYear)}
                    onChange={(e) =>
                      handleEntryChange(
                        index,
                        entryIndex,
                        "previousYear",
                        parseNumber(e.target.value)
                      )
                    }
                  />
                  {item.entries.length > 2 && (
                    <div 
                      style={{ display: "flex", alignItems: "center", cursor: "pointer", marginLeft: "4px" }} 
                      onClick={() => handleRemoveEntry(index, entryIndex)}
                    >
                      <CloseIcon width={16} height={16} />
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}

            <div className={styles.addRowContainer}>
              <div className={styles.addEntryBtn} onClick={() => handleAddEntry(index)}>
                <PlusIcon className={styles.plusIcon} />
                <span style={{ fontWeight: "400", fontSize: "16px", color: "#2D8861" }}>Add Row</span>
              </div>
            </div>

          </div>
        </div>
      ))}
    </div>
  );
};

export default AddNewAdjustment;